import { useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { XIcon } from 'lucide-react';
import { DONOR_APP_URL } from './external-links';
import { AppHomeScreen } from './home/app-home-screen';
import { IPhone } from './iphone';

/**
 * The visit-long invitation to certify a donation, over every website page (mounted in `www/route.tsx`, so closing it
 * holds across navigations without storing anything). Below the snap sheets' z-40, and with no surface of its own:
 * only the phone, the bubble and the close button take clicks, the space around them stays the page's.
 *
 * The homepage's demo phone at a third of its size, loop included: `data-arrived` on `app-phone` is what the CSS keys
 * the settle and the 12 s screen loop on, set outright because `usePhoneStage` observes a `<section>` this has none of.
 */
export function AddDonationFloat() {
	const { t } = useLingui();
	const [dismissed, setDismissed] = useState(false);
	if (dismissed) return null;

	return (
		<div className="pointer-events-none fixed right-4 bottom-3 z-30 max-w-[calc(100vw-2rem)] sm:right-10 sm:bottom-6">
			<a
				href={`${DONOR_APP_URL}/add`}
				target="_blank"
				rel="noreferrer"
				className="pointer-events-none flex items-start gap-2"
			>
				<div className="bg-ink pointer-events-auto relative flex min-w-0 flex-col gap-1.5 rounded-3xl p-4 shadow-[0_12px_32px_0_rgba(46,20,0,0.18)] transition-shadow hover:shadow-[0_16px_40px_0_rgba(46,20,0,0.26)] sm:max-w-[17rem]">
					<span className="text-lg leading-tight font-medium text-white">{t`Certify your donation`}</span>
					<span className="text-sm leading-snug text-white/80">
						{t`If you have made a donation and want to request your certificate, click here.`}
					</span>
					{/* The bubble's tail, pointing at the phone beside it. */}
					<span aria-hidden className="absolute top-1/2 -right-[5px] size-3 -translate-y-1/2 rotate-45 bg-inherit" />
				</div>

				<div className="app-phone pointer-events-auto relative shrink-0" data-arrived="">
					<IPhone label={t`Worldcoo app preview`} className="[--phone-w:7rem] sm:[--phone-w:9rem]">
						<AppHomeScreen zoomClassName="[zoom:0.3532] sm:[zoom:0.4541]" />
					</IPhone>
				</div>
			</a>
			<button
				type="button"
				onClick={() => setDismissed(true)}
				aria-label={t`Close`}
				className="text-ink pointer-events-auto absolute -top-1.5 -left-1.5 grid size-6 place-items-center rounded-full bg-white shadow-[0_4px_12px_0_rgba(46,20,0,0.18)]"
			>
				<XIcon className="size-3.5" />
			</button>
		</div>
	);
}
